import { db } from '@/lib/firebase/config';
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp, Timestamp, onSnapshot, collection, query, getDocs } from 'firebase/firestore';
import { format } from 'date-fns';

/**
 * Customer data in Firestore waiting room
 */
export interface WaitingCustomer {
  customerId: string;
  customerName: string;
  customerPhone?: string;
  joinTime: Timestamp;
  lastHeartbeat: Timestamp;
}

/**
 * Presence Service using Firestore with Heartbeat
 * 
 * NEW APPROACH: Each customer has their own presence document in a subcollection.
 * This eliminates array manipulation conflicts.
 * 
 * Structure: waitingRooms/{coachId-date}/customers/{customerId}
 */
export class PresenceService {
  
  private static HEARTBEAT_INTERVAL = 5000; // 5 seconds
  private static STALE_THRESHOLD = 15000; // 15 seconds - consider customer gone
  
  /**
   * Join the waiting room and start sending heartbeats
   */
  static async joinWaitingRoom(
    coachId: string,
    customerId: string,
    customerName: string,
    customerPhone?: string,
    onConnectionChange?: (connected: boolean) => void
  ): Promise<() => void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    
    // Create customer presence document in subcollection
    const customerPresenceRef = doc(db, 'waitingRooms', waitingRoomId, 'customers', customerId);
    
    const now = Timestamp.now();
    const customerData: WaitingCustomer = {
      customerId,
      customerName: customerName || 'Guest',
      customerPhone,
      joinTime: now,
      lastHeartbeat: now
    };
    
    // Set customer presence (will overwrite if exists)
    await setDoc(customerPresenceRef, customerData);
    
    // Ensure waiting room document exists
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);
    const waitingRoomDoc = await getDoc(waitingRoomRef);
    if (!waitingRoomDoc.exists()) {
      await setDoc(waitingRoomRef, {
        id: waitingRoomId,
        coachId,
        date: today,
        coachStatus: 'open',
        attendedCustomers: [],
        lastUpdated: serverTimestamp()
      });
    }

    onConnectionChange?.(true);

    // Start heartbeat interval
    const heartbeatInterval = setInterval(async () => {
      try {
        await PresenceService.sendHeartbeat(coachId, customerId);
        onConnectionChange?.(true);
      } catch (error) {
        console.error('Heartbeat failed:', error);
        // Don't immediately report disconnected - might be temporary
      }
    }, this.HEARTBEAT_INTERVAL);

    // Return cleanup function
    return () => {
      clearInterval(heartbeatInterval);
      // Remove from waiting room on cleanup
      PresenceService.leaveWaitingRoom(coachId, customerId).catch(() => {});
    };
  }

  /**
   * Send heartbeat - simply update own document (no conflicts possible)
   */
  static async sendHeartbeat(coachId: string, customerId: string): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const customerPresenceRef = doc(db, 'waitingRooms', waitingRoomId, 'customers', customerId);
    
    // Just update lastHeartbeat - setDoc with merge to avoid overwriting other fields
    await setDoc(customerPresenceRef, {
      lastHeartbeat: Timestamp.now()
    }, { merge: true });
  }

  /**
   * Leave the waiting room - just delete own document
   */
  static async leaveWaitingRoom(coachId: string, customerId: string): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const customerPresenceRef = doc(db, 'waitingRooms', waitingRoomId, 'customers', customerId);
    
    await deleteDoc(customerPresenceRef);
  }

  /**
   * Listen to waiting room and filter out stale customers
   * This is what the doctor dashboard uses
   * Now listens to subcollection
   */
  static listenToWaitingRoom(
    coachId: string,
    callback: (customers: WaitingCustomer[]) => void
  ): () => void {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const customersRef = collection(db, 'waitingRooms', waitingRoomId, 'customers');

    const unsubscribe = onSnapshot(customersRef, (snapshot) => {
      const now = Date.now();
      const activeCustomers: WaitingCustomer[] = [];
      
      snapshot.docs.forEach(doc => {
        const data = doc.data() as WaitingCustomer;
        const lastHeartbeat = data.lastHeartbeat?.toDate?.()?.getTime() || 0;
        
        // Only include customers with recent heartbeats
        if ((now - lastHeartbeat) < this.STALE_THRESHOLD) {
          activeCustomers.push(data);
        }
      });
      
      // Sort by join time
      activeCustomers.sort((a, b) => {
        const aTime = a.joinTime?.toDate?.()?.getTime() || 0;
        const bTime = b.joinTime?.toDate?.()?.getTime() || 0;
        return aTime - bTime;
      });
      
      callback(activeCustomers);
    });

    return unsubscribe;
  }

  /**
   * Cleanup stale entries from the waiting room (removes from Firestore)
   * Now deletes stale documents from subcollection
   */
  static async cleanupStaleEntries(coachId: string): Promise<number> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const customersRef = collection(db, 'waitingRooms', waitingRoomId, 'customers');
    
    const snapshot = await getDocs(customersRef);
    const now = Date.now();
    let removedCount = 0;
    
    const deletePromises: Promise<void>[] = [];
    
    snapshot.docs.forEach(doc => {
      const data = doc.data();
      const lastHeartbeat = data.lastHeartbeat?.toDate?.()?.getTime() || 0;
      
      if ((now - lastHeartbeat) >= this.STALE_THRESHOLD) {
        deletePromises.push(deleteDoc(doc.ref));
        removedCount++;
      }
    });
    
    await Promise.all(deletePromises);
    return removedCount;
  }

  /**
   * Listen to customer's own status in waiting room
   */
  static listenToCustomerPresence(
    coachId: string,
    customerId: string,
    callback: (isInRoom: boolean, customersCount: number, ownJoinTimeMs: number | null) => void
  ): () => void {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const customersRef = collection(db, 'waitingRooms', waitingRoomId, 'customers');

    const unsubscribe = onSnapshot(customersRef, (snapshot) => {
      const now = Date.now();
      let isInRoom = false;
      let activeCount = 0;
      let ownJoinTimeMs: number | null = null;

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const lastHeartbeat = data.lastHeartbeat?.toDate?.()?.getTime() || 0;

        if (doc.id === customerId) {
          ownJoinTimeMs = data.joinTime?.toMillis?.() ?? null;
        }
        if ((now - lastHeartbeat) < this.STALE_THRESHOLD) {
          activeCount++;
          if (doc.id === customerId) {
            isInRoom = true;
          }
        }
      });

      callback(isInRoom, activeCount, ownJoinTimeMs);
    });

    return unsubscribe;
  }

  /**
   * Remove a customer from the waiting room (when starting a call)
   */
  static async removeFromWaitingRoom(coachId: string, customerId: string): Promise<void> {
    await this.leaveWaitingRoom(coachId, customerId);
  }

  /**
   * Get/set clinic status (uses Firestore)
   */
  static listenToClinicStatus(
    coachId: string,
    callback: (status: 'open' | 'closed') => void
  ): () => void {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);

    const unsubscribe = onSnapshot(waitingRoomRef, (snapshot) => {
      if (!snapshot.exists()) {
        callback('closed');
        return;
      }
      callback(snapshot.data().coachStatus || 'closed');
    });

    return unsubscribe;
  }

  static async setClinicStatus(coachId: string, status: 'open' | 'closed'): Promise<void> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const waitingRoomId = `${coachId}-${today}`;
    const waitingRoomRef = doc(db, 'waitingRooms', waitingRoomId);

    await setDoc(waitingRoomRef, {
      id: waitingRoomId,
      coachId,
      date: today,
      coachStatus: status,
      lastUpdated: serverTimestamp()
    }, { merge: true });
  }
}
